import { Module, Mutation, Action } from "vuex-class-modules"
import { Modules } from "../modules"
import { store } from "../store"
import { BaseModule } from "@/models/BaseModule"
import { BacklogSprintSetup, BacklogTab, Sprint, TeamType } from "@/models"
import { SprintActions, sprintModule } from "./sprint.module"
import { FilterSprint } from "@/models/Filter/FilterSprint"
import { sessionModule } from "./session.module"

export enum TabsActions {
  GET_TABS = "GET_TABS",
  GET_TABS_WITH_SPRINTS = "GET_TABS_WITH_SPRINTS",
  CLEAR_TABS = "CLEAR_TABS",
  NOT_COMPLETED_SPRINTS_TABS = "NOT_COMPLETED_SPRINTS_TABS",
  GET_REQUESTED_TABS = "GET_REQUESTED_TABS",
  REMOVE_TAB_BY_ID = "REMOVE_TAB_BY_ID",
  ADD_REQUESTED_IN_TABS = "ADD_REQUESTED_IN_TABS",
  GET_TABS_BY_TEAM_TYPE = "GET_TABS_BY_TEAM_TYPE",
  GET_TABS_WITH_SINGLE_LIST = "GET_TABS_WITH_SINGLE_LIST"
}

@Module
class TabsModule extends BaseModule {
  tabs: BacklogTab[] = [
    {
      id: "BacklogProduct",
      label: "Team Backlog",
      filterId: ["STORY", "SPIKE"],
      countOptions: {
        number: "storyAndSpike",
      },
    },
    {
      id: "BacklogBug",
      label: "Bugs",
      filterId: ["BUG"],
      countOptions: {
        number: "bug",
      },
    },
    {
      id: "BacklogSupportCases",
      label: "Support Cases",
      filterId: ["SUPPORT_CASE"],
      countOptions: {
        number: "supportCase",
      },
    },
    {
      id: "BacklogTechDebt",
      label: "Tech Debt",
      filterId: ["TECH_DEBT"],
      countOptions: {
        number: "techDebt",
      },
    },
    {
      id: "BacklogDesignDebt",
      label: "Design Debt",
      filterId: ["DESIGN_DEBT"],
      countOptions: {
        number: "designDebt",
      },
    },
    {
      id: "BacklogDev",
      label: "Dev",
      filterId: ["DEV"],
      countOptions: {
        number: "dev",
      },
    },
    {
      id: "BacklogDevOps",
      label: "DevOps",
      filterId: ["DEV_OPS"],
      countOptions: {
        number: "devOps",
      },
    },
    {
      id: "BacklogOps",
      label: "Ops",
      filterId: ["OPS"],
      countOptions: {
        number: "ops",
      },
    },
    {
      id: "BacklogIncident",
      label: "Incidents",
      filterId: ["INCIDENT"],
      countOptions: {
        number: "incident",
      },
    },
  ]

  tabsOrderByTeamType: { [teamType in TeamType]?: string[] } = {
    DEVOPS: [
      "BacklogProduct",
      "BacklogIncident",
      "BacklogSupportCases",
      "BacklogBug",
      "BacklogTechDebt",
    ],
  }

  orderTabsByTeamType(tabs: BacklogTab[], teamType: TeamType) {
    const tabsOrder = this.tabsOrderByTeamType[teamType]
    if (!tabsOrder) return tabs
    const orderedTabs = tabsOrder
      .map(tabId => tabs.find(tab => tab.id === tabId))
      .filter((tab): tab is BacklogTab => !!tab)
    let orderedIndex = 0
    return tabs.map(tab => tabsOrder.includes(tab.id) ? orderedTabs[orderedIndex++] : tab)
  }

  get [`get/${TabsActions.GET_TABS}`]() {
    const teamType = sessionModule.team?.type ?? "DEVELOPMENT"
    const availableItemTypes = sessionModule.itemConfig[teamType].availableItemTypes
    const filteredTabs = this.tabs.filter(tab => !tab.filterId || tab.filterId.some(type => availableItemTypes.includes(type)))
    return this.orderTabsByTeamType(filteredTabs, teamType)
  }

  get [`get/${TabsActions.GET_TABS_WITH_SINGLE_LIST}`]() {
    const teamType = sessionModule.team?.type ?? "DEVELOPMENT"
    const availableItemTypes = sessionModule.itemConfig[teamType].availableItemTypes
    const filteredTabs = this.orderTabsByTeamType(
      this.tabs.filter(tab => !tab.filterId || tab.filterId.some(type => availableItemTypes.includes(type))),
      teamType
    )
    return [...filteredTabs, {
      id: "BacklogSingleListView",
      label: "Team Backlog",
      filterId: [],
      countOptions: {
        number: "total",
      },
    }]
  }

  get [`get/${TabsActions.GET_TABS_BY_TEAM_TYPE}`]() {
    return (teamType?: TeamType) => {
      const currentTeamType = teamType ?? "DEVELOPMENT"
      const availableItemTypes = sessionModule.itemConfig[currentTeamType].availableItemTypes
      const filteredTabs = this.tabs.filter(tab => !tab.filterId || tab.filterId.some(type => availableItemTypes.includes(type)))
      return this.orderTabsByTeamType(filteredTabs, currentTeamType)
    }
  }

  get [`get/${TabsActions.NOT_COMPLETED_SPRINTS_TABS}`]() {
    const sprints: BacklogTab[] = []
    const notCompletedSprints = sprintModule.notCompletedSprints
    if (!notCompletedSprints) return sprints
    notCompletedSprints
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .forEach((info) => {
        sprints.push({
          id: `BacklogSprint#${info.sequenceNumber}`,
          label: `Sprint #${info.sequenceNumber}`,
          status: info.status,
          startDate: info.startDate,
          endDate: info.endDate,
          backlogSetup: new BacklogSprintSetup(info),
          countOptions: {
            number: info.id,
          },
        })
      })
    return sprints
  }

  @Mutation
  addSprintInTabs({ sprints, beforeTabs } : { sprints: Sprint[], beforeTabs?: boolean}) {
    this.tabs = this.tabs.filter(radio => radio.id.includes("BacklogSprint") === false)
    const sprintsTab: BacklogTab[] = []
    // const notCompletedSprints = sprintModule.notCompletedSprints

    sprints
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .forEach((info) => {
        sprintsTab.push({
          id: `BacklogSprint#${info.sequenceNumber}`,
          label: `Sprint #${info.sequenceNumber}`,
          status: info.status,
          startDate: info.startDate,
          endDate: info.endDate,
          backlogSetup: new BacklogSprintSetup(info),
          countOptions: {
            number: info.id,
          },
        })
      })

    this.tabs = beforeTabs ? [
      ...sprintsTab,
      ...this.tabs,
    ] : [
      ...this.tabs,
      ...sprintsTab,
    ]
  }

  @Mutation
  clearTabs() {
    this.tabs = this.tabs.filter(radio => radio.id.includes("BacklogSprint") === false && radio.id.includes("BacklogStoryPoker") === false)
  }

  @Mutation
  removeTebById(tabId) {
    this.tabs = this.tabs.filter(tab => tab.id !== tabId)
  }

  @Mutation
  addRequestedInTabs() {
    if (this.tabs.find(e => e.id === "BacklogStoryPoker")) return
    this.tabs = [{ id: "BacklogStoryPoker", label: "Requested" }, ...this.tabs]
  }

  @Action
  async [TabsActions.GET_TABS_WITH_SPRINTS]({ teamId, beforeTabs } : { teamId: string, beforeTabs?: boolean }) {
    const sprints = await sprintModule[SprintActions.NOT_COMPLETED_SPRINTS]({
      teamId,
    })
    this.addSprintInTabs({ sprints, beforeTabs })
  }

  @Action
  async [TabsActions.CLEAR_TABS]() {
    this.clearTabs()
  }

  @Action
  async [TabsActions.GET_REQUESTED_TABS](teamId) {
    this.addRequestedInTabs()
    const filterSprint = new FilterSprint({
      includedStatuses: ["NOT_STARTED"],
      teamId,
    })
    const sprintsServerResponse = await sprintModule[SprintActions.SPRINTS_WITH_STATS](filterSprint)
    const sprints = sprintsServerResponse.content.map(sprintWithStats => new Sprint(sprintWithStats.sprint))
    this.addSprintInTabs({
      sprints,
    })
  }

  @Action
  async [TabsActions.REMOVE_TAB_BY_ID](tabId) {
    this.removeTebById(tabId)
  }

  @Action
  async [TabsActions.ADD_REQUESTED_IN_TABS]() {
    this.addRequestedInTabs()
  }
}


export const tabsModule = new TabsModule({ store, name: Modules.TABS })
